import { useEffect, useId, useRef, useState } from 'react';
import '../screens/vibes-v2.css';

interface AppTypeFilterProps {
  types: readonly string[];
  selected: string[];
  total: number;
  count: (type: string) => number;
  onChange: (types: string[]) => void;
}

export function AppTypeFilter({ types, selected, total, count, onChange }: AppTypeFilterProps) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  useEffect(() => {
    if (!open) return;
    const outside = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const focusOutside = (event: FocusEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setOpen(false); trigger.current?.focus(); }
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    document.addEventListener('focusin', focusOutside);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', escape);
      document.removeEventListener('focusin', focusOutside);
    };
  }, [open]);
  const summary = selected.length === 0
    ? 'All categories'
    : selected.length === 1
      ? selected[0]
      : `${selected.length} categories selected`;
  return <div ref={container} className="v2-type-filter">
    <button ref={trigger} type="button" className="v2-type-trigger" aria-expanded={open} aria-controls={panelId}
      aria-label={`Filter by category: ${summary}`} onClick={() => setOpen(!open)}>
      <span>Category: {summary}</span>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={open ? 'M6 15l6-6 6 6' : 'M6 9l6 6 6-6'} /></svg>
    </button>
    {open && <div id={panelId} className="v2-type-options" role="group" aria-label="Categories">
      <p>Select one or more categories</p>
      <label><input type="checkbox" checked={selected.length === 0} onChange={() => onChange([])} /><span>All categories <span className="v2-type-count">({total})</span></span></label>
      {types.map(type => <label key={type}><input type="checkbox" checked={selected.includes(type)} onChange={event => onChange(event.target.checked ? [...selected, type] : selected.filter(item => item !== type))} /><span>{type} <span className="v2-type-count">({count(type)})</span></span></label>)}
      <div className="v2-type-actions">
        <button type="button" className="v2-type-reset" onClick={() => onChange([])}>Reset</button>
        <button type="button" className="v2-type-done" onClick={() => { setOpen(false); trigger.current?.focus(); }}>Done</button>
      </div>
    </div>}
  </div>;
}
